import { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, MousePointer2, Pencil, Eraser, Trash2, Loader2,
  AlertCircle, CheckCircle2, Boxes, ListTree, RefreshCw,
  DoorOpen, DoorClosed, Square as SquareIcon, RectangleHorizontal,
  PanelTopOpen, Magnet, Archive, Spline, StretchHorizontal,
  Link2, Info, Keyboard, ArrowUpRight, Wand2, X, Undo2, Redo2,
  TrendingDown,
  PenLine,
} from 'lucide-react';
import { Button } from '../../ui/button';
import StructureCanvas from './StructureCanvas';
import ContourAlignPanel from './ContourAlignPanel';
import { FloorOverlayControl } from './FloorOverlayControl';
import Structure3DPreview from './Structure3DPreview';
import { WallInspector } from './WallInspector';
import StairsTypePicker from './StairsTypePicker';
import { StairsPanelSection, SkewPanelSection } from './StairsSkewPanels';
import { FixtureToolPicker, FixturesPanelSection } from './FixturesPanel';
import { GhostFloorsControl } from './GhostFloorsControl';
import { FoldableSection } from './FoldableSection';
import { DuplicateFloorControl } from './DuplicateFloorControl';
import { PhaseSnapshotButton } from './PhaseSnapshotButton';
import SubTabVectorize from './SubTabVectorize';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import {
  usePlanEditor, WALL_TYPES, wallStyleFor,
  MENUISERIE_TYPES, openingStyleFor, openingSpecFor, OPENING_MODES, GLAZED_OPENING_TYPES,
} from './PlanEditorContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Sub-tabs ----
const SUBTABS = [
  { key: 'contour',  label: 'Contour',     icon: SquareIcon },
  { key: 'walls',    label: 'Murs',          icon: Boxes },
  { key: 'openings', label: 'Menuiseries',   icon: DoorOpen },
];

// ---- Menuiserie picto map ----
const MENUISERIE_ICON = {
  front_door:    DoorOpen,
  interior_door: DoorClosed,
  closet_door:   Archive,
  french_door:   PanelTopOpen,
  window:        SquareIcon,
  bay_window:    RectangleHorizontal,
};

/**
 * TabStructure — V14.47 Sprint 2 + V14.48 Sprint 3.
 *
 * Sub-tabs : 2a Contours (placeholder) · 2b Murs intérieurs · 2c Menuiseries.
 * Center canvas is shared (StructureCanvas). The toolbar and right panel
 * swap depending on the active sub-tab.
 *
 * Keyboard shortcuts (active in any sub-tab) :
 *   S — Sélectionner   D — Dessiner   E — Gomme   ⌫/⌦ — Supprimer
 */
const TabStructure = ({ projectId, selectedLot, selectedLotId, savedState, onSavedStateChanged, externalSub }) => {
  const isMultiLot = !!selectedLot || !!selectedLotId;
  const { setActiveSubTab, setMode } = usePlanEditor();
  // V14.76 — Switch 2D/3D (inspiration Homebyme).
  const [viewMode, setViewMode] = useState('2d');
  // V14.68.22 — synchro descendante depuis la ligne 2 du menu horizontal.
  useEffect(() => {
    if (externalSub && SUBTABS.some((s) => s.key === externalSub)) {
      setActiveSubTab(externalSub);
      setMode('select');
    }
  }, [externalSub, setActiveSubTab, setMode]);
  const blockingReason = useMemo(() => {
    if (isMultiLot && !selectedLotId) return 'Sélectionnez un lot dans la barre latérale.';
    if (!savedState?.source_file_id) return 'Uploadez un plan dans l\u2019étape 1 · Plan source.';
    return null;
  }, [isMultiLot, selectedLotId, savedState]);

  const bgImageUrl = useMemo(() => {
    if (!savedState?.source_file_id) return null;
    const fid = savedState?.cleaned_file_id || savedState.source_file_id;
    const endpoint = savedState?.cleaned_file_id ? 'render' : 'source';
    const lotQ = selectedLotId ? `&lot_id=${selectedLotId}` : '';
    return `${API}/projects/${projectId}/plan-source/${endpoint}?file_id=${fid}${lotQ}`;
  }, [projectId, savedState, selectedLotId]);

  return (
    <div className="grid grid-cols-12 gap-3 h-full min-h-0" data-testid="tab-structure-root">
      {/* CENTER */}
      <div className="col-span-8 min-h-0 flex flex-col rounded-lg border overflow-hidden"
           style={{ background: 'rgba(0,0,0,0.25)', borderColor: 'var(--c-border)' }}>
        <SubTabPills disabled={!!blockingReason} viewMode={viewMode} setViewMode={setViewMode} />
        {viewMode === '2d' && (
          <ContextualToolbar projectId={projectId} selectedLotId={selectedLotId}
                             disabled={!!blockingReason}
                             cleanedReady={!!savedState?.cleaned_file_id}
                             hasVectorization={!!savedState?.vectorization}
                             savedState={savedState}
                             onSavedStateChanged={onSavedStateChanged} />
        )}
        <div className="flex-1 min-h-0 relative">
          {blockingReason ? (
            <Blocker text={blockingReason} />
          ) : viewMode === '3d' ? (
            <Structure3DPreview bgImageUrl={bgImageUrl} projectId={projectId} lotId={selectedLotId} />
          ) : (
            <StructureCanvas bgImageUrl={bgImageUrl} />
          )}
        </div>
        <KeyboardLegend />
      </div>

      {/* RIGHT */}
      <div className="col-span-4 min-h-0 flex flex-col rounded-lg border"
           style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--c-border)' }}>
        <ContextualPanel projectId={projectId} selectedLotId={selectedLotId}
                         disabled={!!blockingReason} bgImageUrl={bgImageUrl} />
      </div>

      <KeyboardShortcuts disabled={!!blockingReason} />
      <StairsTypePicker />
    </div>
  );
};

// =====================================================================
// SUB-TAB PILLS
// =====================================================================

const SubTabPills = ({ disabled, viewMode, setViewMode }) => {
  const { activeSubTab, setActiveSubTab, setMode } = usePlanEditor();
  const handleClick = useCallback((key) => {
    setActiveSubTab(key);
    setMode('select'); // reset mode on sub-tab switch — modes are sub-tab-specific
  }, [setActiveSubTab, setMode]);

  return (
    <div className="flex items-center gap-1 px-2 py-1.5 border-b"
         style={{ borderColor: 'var(--c-border)', background: 'rgba(255,255,255,0.02)' }}
         data-testid="structure-subtabs">
      {SUBTABS.map((st) => {
        const Icon = st.icon;
        const active = activeSubTab === st.key;
        return (
          <button
            key={st.key}
            type="button"
            onClick={() => handleClick(st.key)}
            className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded text-[11px] border transition-colors ${active
              ? 'bg-amber-500/15 text-amber-200 border-amber-400/40'
              : 'border-white/10 text-gray-300 hover:bg-white/5'} ${disabled ? 'opacity-70' : ''}`}
            data-testid={`subtab-${st.key}`}
          >
            <Icon className="w-3 h-3" />
            {st.label}
          </button>
        );
      })}
      {/* V14.76 — Switch 2D / 3D façon Homebyme */}
      <div className="ml-auto inline-flex rounded overflow-hidden border"
           style={{ borderColor: 'var(--c-border)' }}
           data-testid="structure-view-switch">
        {['2d', '3d'].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setViewMode(m)}
            className={`px-3 h-7 text-[11px] font-bold uppercase transition-colors ${viewMode === m
              ? 'bg-teal-500/25 text-teal-200'
              : 'text-gray-400 hover:bg-white/5'}`}
            data-testid={`structure-view-${m}`}
            title={m === '3d' ? 'Vue 3D temps réel de la structure tracée' : 'Édition 2D sur plan'}
          >
            {m.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
};

// =====================================================================
// CONTEXTUAL TOOLBAR
// =====================================================================

const ContextualToolbar = ({ projectId, selectedLotId, disabled, cleanedReady, hasVectorization,
                             savedState, onSavedStateChanged }) => {
  const { activeSubTab } = usePlanEditor();
  if (activeSubTab === 'walls') {
    return <WallsToolbar projectId={projectId} selectedLotId={selectedLotId}
                         disabled={disabled} cleanedReady={cleanedReady}
                         hasVectorization={hasVectorization}
                         savedState={savedState} onSavedStateChanged={onSavedStateChanged} />;
  }
  if (activeSubTab === 'openings') {
    return <OpeningsToolbar disabled={disabled} cleanedReady={cleanedReady} />;
  }
  return <ContourToolbar projectId={projectId} selectedLotId={selectedLotId}
                         disabled={disabled} cleanedReady={cleanedReady} />;
};

const ContextualPanel = ({ projectId, selectedLotId, disabled, bgImageUrl }) => {
  const { activeSubTab } = usePlanEditor();
  if (activeSubTab === 'walls') return <WallsPanel projectId={projectId} selectedLotId={selectedLotId} disabled={disabled} bgImageUrl={bgImageUrl} />;
  if (activeSubTab === 'openings') return <OpeningsPanel projectId={projectId} disabled={disabled} />;
  return <ContourPanel projectId={projectId} selectedLotId={selectedLotId} />;
};

// =====================================================================
// WALLS TOOLBAR — Sprint 2
// =====================================================================

const WallsToolbar = ({ projectId, selectedLotId, disabled, cleanedReady, hasVectorization,
                        savedState, onSavedStateChanged }) => {
  const { mode, setMode, walls, actions, saving, isDirty,
          canUndoWalls, canRedoWalls, wallsHistoryDepth } = usePlanEditor();
  const [detecting, setDetecting] = useState(false);
  const [autoTraceOpen, setAutoTraceOpen] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [lastDetection, setLastDetection] = useState(null);
  const [lastNormalize, setLastNormalize] = useState(null);

  // V14.68.21 — Undo/redo keyboard shortcuts (⌘Z / ⌘⇧Z, focus-safe)
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing in an input/textarea
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod || e.key.toLowerCase() !== 'z') return;
      e.preventDefault();
      if (e.shiftKey) {
        if (actions.redoWalls?.()) toast.info('Restauré (redo murs)');
      } else {
        if (actions.undoWalls?.()) toast.info('Annulé (undo murs)');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [actions]);

  const handleDetect = useCallback(async () => {
    if (disabled) return;
    setDetecting(true);
    try {
      const url = `${API}/projects/${projectId}/plan-source/detect-interior-walls${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      const r = await axios.post(url, null, { withCredentials: true });
      const det = r.data?.detection;
      if (!det || !Array.isArray(det.segments) || det.segments.length === 0) {
        toast.warning('Aucun mur intérieur détecté — vous pouvez en dessiner manuellement.');
        setLastDetection({ count: 0, confidence: det?.confidence || 0 });
        return;
      }
      actions.replaceFromDetection(det.segments, { replace: false });
      setLastDetection({ count: det.segments.length, confidence: det.confidence || 0 });
      toast.success(`${det.segments.length} mur(s) détecté(s) · confiance ${Math.round((det.confidence || 0) * 100)}%`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429) toast.error(detail || 'Patientez quelques secondes avant une nouvelle détection.');
      else toast.error(detail || 'Détection IA indisponible');
    } finally {
      setDetecting(false);
    }
  }, [projectId, selectedLotId, actions, disabled]);

  const handleClear = useCallback(() => {
    if (walls.length === 0) return;
    if (!window.confirm(`Supprimer les ${walls.length} murs en cours d\u2019édition ?`)) return;
    actions.clearAllWalls();
  }, [walls.length, actions]);

  const handleNormalize = useCallback(async () => {
    if (disabled || walls.length === 0) return;
    setNormalizing(true);
    try {
      const url = `${API}/projects/${projectId}/plan-source/structure/normalize-walls${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      const r = await axios.post(url,
        { endpoint_merge_radius: 0.012, angle_snap_tolerance_deg: 8 },
        { withCredentials: true });
      const stats = r.data?.stats || {};
      const newWalls = r.data?.structure?.walls || [];
      actions.setWallsFromServer(newWalls);
      setLastNormalize(stats);
      if (stats.snapped_angles === 0 && stats.merged_endpoints === 0) {
        toast.info('Tracés déjà normalisés (aucun ajustement nécessaire).');
      } else {
        toast.success(
          `Normalisation : ${stats.snapped_angles} mur(s) redressé(s) · ${stats.merged_endpoints} point(s) joint(s)`,
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Échec de la normalisation');
    } finally {
      setNormalizing(false);
    }
  }, [projectId, selectedLotId, disabled, walls.length, actions]);

  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b flex-wrap"
         style={{ borderColor: 'var(--c-border)', background: 'rgba(255,255,255,0.02)' }}
         data-testid="structure-toolbar">
      {/* V14.70 — la vectorisation est désormais en 1e·Préparation.
          Le bouton IA consomme automatiquement l'artefact préparé. */}
      {/* V15.10 — Pré-tracé automatique (vectorisation OpenCV) déplacé ici depuis l'ex-1e. */}
      <button type="button" onClick={() => setAutoTraceOpen(true)} disabled={disabled}
              className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded text-sky-200 border border-sky-400/40 hover:bg-sky-500/10 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shrink-0"
              data-testid="auto-trace-walls-btn"
              title="Pré-tracer automatiquement les murs depuis l'image (OpenCV, sans IA) — vous ajustez ensuite à la main">
        <Wand2 className="w-3 h-3" />
        <span className="hidden 2xl:inline">Pré-tracer (auto)</span>
      </button>
      <Dialog open={autoTraceOpen} onOpenChange={setAutoTraceOpen}>
        <DialogContent className="max-w-md" data-testid="auto-trace-dialog">
          <DialogHeader>
            <DialogTitle className="text-[13px]">Pré-tracé automatique des murs</DialogTitle>
            <DialogDescription className="text-[10.5px]">
              Optionnel : extrait des segments depuis le plan image pour gagner du temps, puis envoyez-les dans le tracé.
            </DialogDescription>
          </DialogHeader>
          <SubTabVectorize projectId={projectId} selectedLotId={selectedLotId}
                           savedState={savedState} onSavedStateChanged={onSavedStateChanged}
                           onSent={() => setAutoTraceOpen(false)} />
        </DialogContent>
      </Dialog>
      <Button onClick={handleDetect} disabled={disabled || detecting} size="sm"
              className="h-7 bg-amber-600 hover:bg-amber-700 text-white text-[11px]"
              data-testid="detect-walls-btn"
              title={hasVectorization
                ? "Détection IA améliorée : envoie l'original + la version pré-tracée à Gemini."
                : "Détection IA standard. Astuce : lancez d'abord « Pré-tracer (auto) » pour une reconnaissance nettement plus fiable."}>
        {detecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
        {detecting ? 'Détection…' : (
          <>
            <span className="hidden 2xl:inline">{hasVectorization ? 'Détecter les Murs (IA ✨ vectorisée)' : 'Détecter les Murs (IA)'}</span>
            <span className="2xl:hidden">IA Murs{hasVectorization ? ' ✨' : ''}</span>
          </>
        )}
      </Button>
      <div className="w-px h-5 bg-white/10 mx-1" />
      {/* V14.68.21 — Undo/Redo sur l'édition manuelle des murs (add/update/
          delete/clear/AI-replace). Persiste ~40 snapshots max. Raccourcis
          clavier ⌘Z / Ctrl+Z (undo) et ⌘⇧Z / Ctrl+⇧Z (redo). */}
      <button type="button" onClick={() => { if (actions.undoWalls?.()) toast.info('Annulé'); }}
              disabled={disabled || !canUndoWalls}
              data-testid="walls-undo-btn"
              title={`Annuler (⌘Z) · ${wallsHistoryDepth} action${wallsHistoryDepth > 1 ? 's' : ''} annulable${wallsHistoryDepth > 1 ? 's' : ''}`}
              className="inline-flex items-center justify-center w-7 h-7 rounded text-[11px] transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/[0.06]"
              style={{ color: canUndoWalls ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.35)' }}
              aria-label="Annuler la dernière modification des murs">
        <Undo2 className="w-3.5 h-3.5" />
      </button>
      <button type="button" onClick={() => { if (actions.redoWalls?.()) toast.info('Restauré'); }}
              disabled={disabled || !canRedoWalls}
              data-testid="walls-redo-btn"
              title="Rétablir (⌘⇧Z)"
              className="inline-flex items-center justify-center w-7 h-7 rounded text-[11px] transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/[0.06]"
              style={{ color: canRedoWalls ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.35)' }}
              aria-label="Rétablir la modification annulée">
        <Redo2 className="w-3.5 h-3.5" />
      </button>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolBtn icon={MousePointer2} label="Sélectionner" hint="S" value="select" mode={mode}
               onClick={setMode} disabled={disabled} testId="tool-select" />
      <ToolBtn icon={Pencil} label="Ligne" hint="D" value="draw" mode={mode}
               onClick={setMode} disabled={disabled} testId="tool-draw-line"
               title="Tracer un mur unitaire (clic départ → clic fin)" />
      <ToolBtn icon={Spline} label="Polyligne" hint="P" value="draw-polyline-walls" mode={mode}
               onClick={(v) => { actions.cancelPolygonDraft(); setMode(v); }}
               disabled={disabled} testId="tool-draw-polyline"
               title="Tracer plusieurs murs chaînés (clics successifs · double-clic ou Entrée pour terminer · Échap pour annuler)" />
      <ToolBtn icon={Eraser} label="Gomme" hint="E" value="erase" mode={mode}
               onClick={setMode} disabled={disabled} testId="tool-erase" />
      <div className="w-px h-5 bg-white/10 mx-1" />
      {/* Sprint 26C — Stair placement. Same affordance as in /maison/:id,
          available in 2b Murs so users can drop stairs alongside the wall
          structure. Clicking the canvas in this mode opens the kind
          picker (Droit / Tournant / Colimaçon). */}
      <ToolBtn icon={StretchHorizontal} label="Escalier" value="place:stairs" mode={mode}
               onClick={setMode} disabled={disabled} testId="tool-place-stairs"
               title="Cliquez sur le canvas pour poser un escalier — choisissez ensuite Droit / Tournant / Colimaçon" />
      {/* V14.69 Phase 2 — Ligne de brisure (mur de mansarde) : 2 clics. */}
      <ToolBtn icon={TrendingDown} label="Brisure" value="draw:skew" mode={mode}
               onClick={setMode} disabled={disabled} testId="tool-draw-skew"
               title="Tracer une ligne de brisure de mansarde (clic départ → clic fin) — HSP basse réglable dans le panneau droit" />
      {/* V15.08 — Éléments fixes typés (îlot, cheminée, colonne, placard, douche…). */}
      <FixtureToolPicker mode={mode} setMode={setMode} disabled={disabled} />
      <div className="w-px h-5 bg-white/10 mx-1" />
      <button type="button" onClick={handleNormalize}
              disabled={disabled || normalizing || walls.length === 0}
              className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded text-emerald-300 border border-emerald-400/40 hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="normalize-walls-btn"
              title="Joindre les points proches + redresser les murs à 90°">
        {normalizing
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <Magnet className="w-3 h-3" />}
        <span className="hidden 2xl:inline">Normaliser</span>
      </button>
      <button type="button" onClick={handleClear}
              disabled={disabled || walls.length === 0}
              className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded text-rose-300 border border-rose-400/30 hover:bg-rose-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="tool-clear-all" title="Effacer tous les murs">
        <Trash2 className="w-3 h-3" />
        <span className="hidden 2xl:inline">Tout effacer</span>
      </button>
      <div className="ml-auto flex items-center gap-2 text-[10.5px]">
        {cleanedReady && (
          <span className="hidden 2xl:inline-flex items-center gap-1 text-teal-300"
                title="Le canvas utilise la version nettoyée du plan">
            <CheckCircle2 className="w-3 h-3" /> Plan nettoyé
          </span>
        )}
        {lastDetection && (
          <span className="text-gray-400 hidden 2xl:inline" data-testid="detection-meta">
            IA · {lastDetection.count} murs · {Math.round((lastDetection.confidence || 0) * 100)}%
          </span>
        )}
        {lastNormalize && (lastNormalize.snapped_angles > 0 || lastNormalize.merged_endpoints > 0) && (
          <span className="text-emerald-300 hidden 2xl:inline" data-testid="normalize-meta">
            Norm · {lastNormalize.snapped_angles}↺ · {lastNormalize.merged_endpoints}⊕
          </span>
        )}
        <SaveIndicator saving={saving} isDirty={isDirty} />
      </div>

      <PhaseSnapshotButton projectId={projectId} selectedLotId={selectedLotId}
                           phase="structure" disabled={disabled} />
    </div>
  );
};

// =====================================================================
// OPENINGS TOOLBAR — Sprint 3
// =====================================================================

const OpeningsToolbar = ({ disabled, cleanedReady }) => {
  const { mode, setMode, openings, actions, saving, isDirty, lastSyncReport,
          menuiserieSnap, setMenuiserieSnap } = usePlanEditor();

  const handlePick = useCallback((type) => {
    setMode(`place:${type}`);
  }, [setMode]);

  const handleClear = useCallback(() => {
    if (openings.length === 0) return;
    if (!window.confirm(`Supprimer les ${openings.length} menuiseries en cours d\u2019édition ?`)) return;
    actions.clearAllOpenings();
  }, [openings.length, actions]);

  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b flex-wrap"
         style={{ borderColor: 'var(--c-border)', background: 'rgba(255,255,255,0.02)' }}
         data-testid="openings-toolbar">
      <ToolBtn icon={MousePointer2} label="Sélectionner" hint="S" value="select" mode={mode}
               onClick={setMode} disabled={disabled} testId="tool-select" />
      <label className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded border border-emerald-400/30 cursor-pointer"
             title="Quand activé, les menuiseries se collent automatiquement au mur le plus proche et adoptent son orientation. Désactivez pour un placement libre.">
        <input type="checkbox" checked={menuiserieSnap}
               onChange={(e) => setMenuiserieSnap(e.target.checked)}
               className="w-3 h-3 accent-emerald-500"
               data-testid="menuiserie-snap-toggle" />
        <Magnet className={`w-3 h-3 ${menuiserieSnap ? 'text-emerald-300' : 'text-gray-400'}`} />
        <span className={`hidden xl:inline ${menuiserieSnap ? 'text-emerald-300 font-medium' : 'text-gray-400'}`}>
          Snap {menuiserieSnap ? 'ON' : 'OFF'}
        </span>
      </label>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <span className="text-[10px] text-gray-400 mr-1 hidden 2xl:inline">Bibliothèque :</span>
      {MENUISERIE_TYPES.map((t) => {
        const Icon = MENUISERIE_ICON[t.value] || SquareIcon;
        const active = mode === `place:${t.value}`;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => handlePick(t.value)}
            disabled={disabled}
            className={`inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active
              ? 'border-white/50 text-white'
              : 'border-white/10 text-gray-200 hover:bg-white/5'}`}
            style={active ? { background: `${t.color}33`, boxShadow: `inset 0 0 0 1px ${t.color}` } : { background: `${t.color}15` }}
            data-testid={`opening-pick-${t.value}`}
            title={`${t.label} — cliquez puis déposez sur le plan`}
          >
            <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: t.color }} />
            {/* V14.81 — abréviation (PE, PI…) sur écrans < 2xl */}
            <span className="hidden 2xl:inline">{t.label}</span>
            <span className="2xl:hidden font-semibold" style={{ color: t.color }}>{t.short}</span>
          </button>
        );
      })}
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolBtn icon={Eraser} label="Gomme" hint="E" value="erase" mode={mode}
               onClick={setMode} disabled={disabled} testId="tool-erase" />
      <button type="button" onClick={handleClear}
              disabled={disabled || openings.length === 0}
              className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded text-rose-300 border border-rose-400/30 hover:bg-rose-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="openings-clear-all" title="Effacer toutes les menuiseries">
        <Trash2 className="w-3 h-3" />
        <span className="hidden 2xl:inline">Tout effacer</span>
      </button>
      <div className="ml-auto flex items-center gap-2 text-[10.5px]">
        {lastSyncReport?.created?.length > 0 && (
          <span className="inline-flex items-center gap-1 text-emerald-300"
                title="Matériauthèque mise à jour" data-testid="materialtheque-sync-flash">
            <Link2 className="w-3 h-3" /> +{lastSyncReport.created.length} <span className="hidden 2xl:inline">slot(s) Matériauthèque</span>
          </span>
        )}
        {cleanedReady && (
          <span className="hidden 2xl:inline-flex items-center gap-1 text-teal-300"
                title="Le canvas utilise la version nettoyée du plan">
            <CheckCircle2 className="w-3 h-3" /> Plan nettoyé
          </span>
        )}
        <SaveIndicator saving={saving} isDirty={isDirty} />
      </div>
    </div>
  );
};

// =====================================================================
// CONTOUR TOOLBAR (2a) — V14.52 Sprint 7
// =====================================================================

const ContourToolbar = ({ cleanedReady, projectId, selectedLotId, disabled }) => {
  const { mode, setMode, outerPolygon, terrainPolygon, polygonDraft, actions, saving, isDirty } = usePlanEditor();
  const [detecting, setDetecting] = useState(false);

  const handleDetect = useCallback(async () => {
    if (disabled) return;
    setDetecting(true);
    try {
      const url = `${API}/projects/${projectId}/plan-source/detect-rooms${selectedLotId ? `?lot_id=${selectedLotId}` : ''}`;
      const r = await axios.post(url, null, { withCredentials: true });
      const dets = r.data?.detection?.rooms || [];
      if (dets.length === 0) {
        toast.warning('Aucune pièce détectée pour déduire un contour. Dessinez-le manuellement.');
        return;
      }
      // Compute the convex hull of all room vertices as a rough outer contour.
      const allPts = dets.flatMap((d) => d.polygon || []);
      if (allPts.length < 3) {
        toast.warning('Pas assez de points pour déduire un contour automatique.');
        return;
      }
      const hull = computeConvexHull(allPts);
      if (hull.length < 3) {
        toast.warning('Contour automatique invalide. Dessinez-le manuellement.');
        return;
      }
      actions.setOuterFromDetection(hull);
      toast.success(`Contour déduit (${hull.length} sommets)`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || 'Détection IA indisponible');
    } finally {
      setDetecting(false);
    }
  }, [projectId, selectedLotId, actions, disabled]);

  const handleClear = useCallback(() => {
    if (outerPolygon.length === 0) return;
    if (!window.confirm('Effacer le contour extérieur ?')) return;
    actions.clearOuterPolygon();
  }, [outerPolygon.length, actions]);

  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b flex-wrap"
         style={{ borderColor: 'var(--c-border)', background: 'rgba(255,255,255,0.02)' }}
         data-testid="contour-toolbar">
      <Button onClick={handleDetect} disabled={disabled || detecting} size="sm"
              className="h-7 bg-amber-600 hover:bg-amber-700 text-white text-[11px]"
              data-testid="detect-contour-btn">
        {detecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
        {detecting ? 'Détection…' : 'Détecter le Contour (IA)'}
      </Button>
      <div className="w-px h-5 bg-white/10 mx-1" />
      <ToolBtn icon={MousePointer2} label="Sélectionner" hint="S" value="select" mode={mode}
               onClick={setMode} disabled={disabled} testId="contour-tool-select" />
      <ToolBtn icon={Pencil} label="Dessiner" hint="D" value="draw-outer-polygon" mode={mode}
               onClick={setMode} disabled={disabled} testId="contour-tool-draw" />
      {/* Sprint 26D — Terrain polygon tool (visible alongside the
          building outline tool; the user can flip between drawing the
          plot boundary and the building footprint without leaving 2a). */}
      <ToolBtn icon={Spline} label="Terrain" hint="T" value="draw-terrain-polygon" mode={mode}
               onClick={setMode} disabled={disabled} testId="contour-tool-terrain"
               title="Tracer la limite de propriété (jardin / terrain) — typiquement sur le RDC" />
      {polygonDraft.length > 0 && (
        <button type="button" onClick={() => actions.cancelPolygonDraft()}
                className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded text-amber-300 border border-amber-400/30 hover:bg-amber-500/10"
                data-testid="contour-cancel-draft">
          Annuler dessin
        </button>
      )}
      <div className="w-px h-5 bg-white/10 mx-1" />
      <button type="button" onClick={handleClear}
              disabled={disabled || outerPolygon.length === 0}
              className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded text-rose-300 border border-rose-400/30 hover:bg-rose-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="contour-clear-btn" title="Effacer le contour extérieur">
        <Trash2 className="w-3 h-3" />
        <span className="hidden 2xl:inline">Effacer le contour</span>
        <span className="2xl:hidden">Contour</span>
      </button>
      <button type="button"
              onClick={() => { if (window.confirm('Effacer le polygone du terrain ?')) actions.clearTerrainPolygon(); }}
              disabled={disabled || !terrainPolygon || terrainPolygon.length === 0}
              className="inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded text-amber-300 border border-amber-400/30 hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="terrain-clear-btn"
              title="Effacer le polygone du terrain (limite de propriété)">
        <Trash2 className="w-3 h-3" />
        <span className="hidden 2xl:inline">Effacer le terrain</span>
        <span className="2xl:hidden">Terrain</span>
      </button>
      <div className="ml-auto flex items-center gap-2 text-[10.5px]">
        {cleanedReady && (
          <span className="inline-flex items-center gap-1 text-teal-300">
            <CheckCircle2 className="w-3 h-3" /> Plan nettoyé
          </span>
        )}
        <SaveIndicator saving={saving} isDirty={isDirty} />
      </div>
    </div>
  );
};

// =====================================================================
// CONTOUR PANEL (2a) — V14.52 Sprint 7
// =====================================================================

const ContourPanel = ({ projectId, selectedLotId }) => {
  const { outerPolygon, polygonDraft, mode } = usePlanEditor();
  return (
    <div className="flex flex-col h-full min-h-0 p-3" data-testid="contour-panel">
      <h4 className="text-[12px] font-semibold flex items-center gap-1.5"
          style={{ color: 'var(--c-gold)' }}>
        <SquareIcon className="w-3.5 h-3.5" /> Contour extérieur
      </h4>
      <div className="mt-2 text-[10.5px]">
        {outerPolygon.length === 0 ? (
          <p className="text-gray-400 italic">
            Aucun contour défini. Cliquez « <span className="text-amber-300">Détecter le Contour (IA)</span> »
            ou passez en mode <span className="text-amber-300">Dessiner</span> pour tracer le polygone
            d&apos;enveloppe (clic pour chaque sommet, double-clic ou clic sur le 1er point pour fermer).
          </p>
        ) : (
          <div>
            <div className="text-teal-300 inline-flex items-center gap-1 mb-1.5">
              <CheckCircle2 className="w-3 h-3" />
              Contour validé · {outerPolygon.length} sommets
            </div>
            <p className="text-gray-400">
              Ce polygone délimite la zone du lot ; tout ce qui est dehors sera ignoré
              lors de la génération du masque ControlNet (parties communes, cages, etc.).
            </p>
          </div>
        )}
      </div>
      <ContourAlignPanel />
      <div className="mt-3"><FloorOverlayControl projectId={projectId} selectedLotId={selectedLotId} /></div>
      {mode === 'draw-outer-polygon' && polygonDraft.length > 0 && (
        <div className="mt-2 text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-400/20 rounded px-1.5 py-1"
             data-testid="contour-draft-status">
          Dessin en cours · {polygonDraft.length} sommet(s).{' '}
          <span className="text-gray-400">Double-cliquez pour fermer le polygone.</span>
        </div>
      )}
    </div>
  );
};

/** Convex hull (Andrew's monotone chain). Returns the points in CCW order. */
function computeConvexHull(points) {
  if (!points || points.length < 3) return points || [];
  const pts = points.slice().sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  upper.pop(); lower.pop();
  return lower.concat(upper);
}

// =====================================================================
// SHARED COMPONENTS
// =====================================================================

const ToolBtn = ({ icon: Icon, label, hint, value, mode, onClick, disabled, testId }) => {
  const active = mode === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      disabled={disabled}
      title={hint ? `${label} (${hint})` : label}
      className={`inline-flex items-center gap-1 px-2 h-7 text-[10.5px] rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active
        ? 'bg-amber-500/15 text-amber-200 border-amber-400/40'
        : 'border-white/10 text-gray-300 hover:bg-white/5'}`}
      data-testid={testId}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      {/* V14.81 — libellé masqué sur écrans < 2xl (icône + tooltip suffisent) */}
      <span className="hidden 2xl:inline">{label}</span>
      {hint && <kbd className="ml-0.5 px-1 text-[9px] rounded bg-white/5 border border-white/10 text-gray-400 hidden 2xl:inline">{hint}</kbd>}
    </button>
  );
};

const SaveIndicator = ({ saving, isDirty }) => {
  if (saving) return <span className="text-amber-300 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Sauvegarde…</span>;
  if (isDirty) return <span className="text-amber-300/80 inline-flex items-center gap-1" data-testid="structure-dirty"><RefreshCw className="w-3 h-3" /> Modifié</span>;
  return <span className="text-teal-300/80 inline-flex items-center gap-1" data-testid="structure-saved"><CheckCircle2 className="w-3 h-3" /> Enregistré</span>;
};

const Blocker = ({ text }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center text-[12px] text-gray-400">
    <AlertCircle className="w-6 h-6 text-amber-400" />
    <div className="max-w-[32ch]">{text}</div>
  </div>
);

const KeyboardLegend = () => (
  <div className="px-2.5 py-1 border-t flex items-center gap-2 text-[9.5px] text-gray-500"
       style={{ borderColor: 'var(--c-border)' }}>
    <Keyboard className="w-3 h-3" />
    Raccourcis :
    <kbd className="px-1 rounded bg-white/5 border border-white/10">S</kbd> Sélection ·
    <kbd className="px-1 rounded bg-white/5 border border-white/10">D</kbd> Dessiner mur ·
    <kbd className="px-1 rounded bg-white/5 border border-white/10">E</kbd> Gomme ·
    <kbd className="px-1 rounded bg-white/5 border border-white/10">Suppr</kbd> Supprimer sélection
  </div>
);

// =====================================================================
// WALLS PANEL — Sprint 2
// =====================================================================

const WallsPanel = ({ projectId, selectedLotId, disabled, bgImageUrl }) => {
  const {
    walls, selectedWallId, setSelectedWallId,
    hoveredWallId, setHoveredWallId, actions,
    endpointSnap, setEndpointSnap,
    drawWallType, setDrawWallType, wallFaceSnap, setWallFaceSnap, planDefaults,
    endpointSnapRadius, setEndpointSnapRadius,
    autoRooms, setAutoRooms,
    showDimensions, setShowDimensions,
    defaultWallThicknessPx,
  } = usePlanEditor();

  const grouped = useMemo(() => {
    const g = { porteur: [], cloison_fine: [], cloison_basse: [], mur_invisible: [] };
    walls.forEach((w) => { (g[w.type] || g.cloison_fine).push(w); });
    return g;
  }, [walls]);

  const aiCount = walls.filter((w) => w.source === 'ai').length;
  const userCount = walls.filter((w) => w.source === 'user').length;

  // V14.57 Sprint 12 — Compute endpoint clusters from current walls.
  // Two endpoints within `endpointSnapRadius` belong to the same cluster.
  const endpointClusters = useMemo(() => {
    if (!walls || walls.length === 0) return [];
    const eps = [];
    walls.forEach((w, i) => {
      eps.push({ x: w.x1, y: w.y1, wallId: w.id, ep: 'p1', wIdx: i });
      eps.push({ x: w.x2, y: w.y2, wallId: w.id, ep: 'p2', wIdx: i });
    });
    const r2 = endpointSnapRadius * endpointSnapRadius;
    const parent = eps.map((_, i) => i);
    const find = (a) => { while (parent[a] !== a) { parent[a] = parent[parent[a]]; a = parent[a]; } return a; };
    for (let i = 0; i < eps.length; i += 1) {
      for (let j = i + 1; j < eps.length; j += 1) {
        const dx = eps[i].x - eps[j].x;
        const dy = eps[i].y - eps[j].y;
        if (dx * dx + dy * dy <= r2) {
          const ri = find(i); const rj = find(j);
          if (ri !== rj) parent[ri] = rj;
        }
      }
    }
    const groups = new Map();
    eps.forEach((ep, i) => {
      const r = find(i);
      if (!groups.has(r)) groups.set(r, []);
      groups.get(r).push(ep);
    });
    return Array.from(groups.values()).map((group, idx) => {
      const cx = group.reduce((s, e) => s + e.x, 0) / group.length;
      const cy = group.reduce((s, e) => s + e.y, 0) / group.length;
      return { idx, count: group.length, cx, cy, members: group };
    });
  }, [walls, endpointSnapRadius]);

  const joinedCount = endpointClusters.filter((c) => c.count >= 2).length;
  const isolatedCount = endpointClusters.filter((c) => c.count === 1).length;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="walls-panel">
      <FoldableSection
        title={`Murs détectés · ${walls.length}`}
        icon={ListTree}
        iconClass="text-[color:var(--c-gold)]"
        testid="walls-header-section"
      >
        <div className="text-[10.5px] text-gray-400 mt-0.5">
          IA {aiCount} · Manuel {userCount}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {WALL_TYPES.map((t) => (
            <span key={t.value}
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: `${t.color}22`, color: t.color }}>
              <span className="w-2 h-2 rounded-sm" style={{ background: t.color }} />
              {t.label} · {grouped[t.value]?.length || 0}
            </span>
          ))}
        </div>
        {/* V14.76 — Pièces automatiques (zone fermée → pièce, split → 2 pièces) */}
        <label className="mt-2 flex items-center gap-1.5 text-[10.5px] cursor-pointer"
               title="Quand une zone se ferme, elle est enregistrée automatiquement comme pièce. Un mur qui coupe une pièce en deux la scinde (l'ancienne est conservée, une nouvelle est créée).">
          <input type="checkbox" checked={autoRooms}
                 onChange={(e) => setAutoRooms(e.target.checked)}
                 className="w-3 h-3 accent-teal-500"
                 data-testid="auto-rooms-toggle" />
          <span className={autoRooms ? 'text-teal-300 font-medium' : 'text-gray-400'}>
            Pièces automatiques {autoRooms ? 'ON' : 'OFF'}
          </span>
        </label>
        {/* V14.79 — Cotation automatique (longueur des murs en m) */}
        <label className="mt-1 flex items-center gap-1.5 text-[10.5px] cursor-pointer"
               title="Affiche la longueur réelle (m) le long de chaque mur — nécessite un plan calibré (2a · Contours).">
          <input type="checkbox" checked={showDimensions}
                 onChange={(e) => setShowDimensions(e.target.checked)}
                 className="w-3 h-3 accent-amber-500"
                 data-testid="wall-dimensions-toggle" />
          <span className={showDimensions ? 'text-amber-300 font-medium' : 'text-gray-400'}>
            Cotes {showDimensions ? 'ON' : 'OFF'}
          </span>
        </label>
      </FoldableSection>

      {/* V14.76 — Inspecteur du mur sélectionné (épaisseur + soupente) */}
      {!disabled && selectedWallId && <WallInspector bgImageUrl={bgImageUrl} />}

      {/* V17.2 — Calque d'étage : enveloppe, murs séparatifs, lots voisins (vérif que le lot colle au plateau) */}
      <FloorOverlayControl projectId={projectId} selectedLotId={selectedLotId} disabled={disabled} />

      {/* V14.72 — Superposition étages (vérif alignement murs extérieurs) */}
      <GhostFloorsControl projectId={projectId} selectedLotId={selectedLotId} disabled={disabled} />

      {/* V14.78 — Duplication de la structure porteuse depuis un autre niveau */}
      <DuplicateFloorControl projectId={projectId} selectedLotId={selectedLotId} disabled={disabled} />

      {/* V14.69 Phases 1&2 — Escaliers (direction/liaison) + Brisures mansarde */}
      <StairsPanelSection disabled={disabled} />
      <SkewPanelSection disabled={disabled} />
      <FixturesPanelSection disabled={disabled} />

      {/* V14.57 Sprint 12 — Endpoints + Snap toggle */}
      {/* V14.91 — Type de mur tracé (épaisseur affichée) + accrochage sur face */}
      <FoldableSection
        title="Tracé de mur"
        icon={PenLine}
        iconClass="text-amber-300"
        badge={<span className="text-[9.5px] text-gray-400">{wallStyleFor(drawWallType).label}{planDefaults?.walls?.[drawWallType] ? ` · ${planDefaults.walls[drawWallType]} cm` : ''}</span>}
        defaultOpen
        testid="wall-draw-section"
      >
        <div className="space-y-1.5" data-testid="wall-draw-settings">
          <div className="flex items-center gap-1 rounded-md p-0.5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }} role="tablist">
            {WALL_TYPES.map((t) => {
              const active = drawWallType === t.value;
              const cm = planDefaults?.walls?.[t.value];
              return (
                <button key={t.value} type="button" role="tab" aria-selected={active}
                        onClick={() => setDrawWallType(t.value)}
                        data-testid={`draw-wall-type-${t.value}`}
                        title={`${t.description}${cm ? ` — ${cm} cm` : ''}`}
                        className="flex-1 text-[10px] px-1.5 py-1 rounded transition-colors leading-tight"
                        style={active ? { background: t.color, color: '#0F1115', fontWeight: 700 } : { color: t.color }}>
                  <span className="block truncate">{t.label}</span>
                  <span className="block text-[9px] opacity-80">{cm ? `${cm} cm` : '—'}</span>
                </button>
              );
            })}
          </div>
          <label className="flex items-center justify-between gap-2 text-[10px] text-gray-300">
            <span title="Quand l'extrémité tracée approche un mur existant, elle vient buter contre sa face (plus de chevauchement cloison/porteur).">Accrochage face de mur</span>
            <select value={wallFaceSnap} onChange={(e) => setWallFaceSnap(e.target.value)}
                    className="text-[10px] rounded px-1.5 py-0.5 bg-black/40 border border-white/10 text-gray-100"
                    data-testid="wall-face-snap-select">
              <option value="auto">Auto (face la plus proche)</option>
              <option value="left">Face gauche</option>
              <option value="center">Axe (centre)</option>
              <option value="right">Face droite</option>
              <option value="off">Désactivé</option>
            </select>
          </label>
        </div>
      </FoldableSection>
      <FoldableSection
        title="Points d'extrémités"
        icon={Magnet}
        iconClass="text-emerald-300"
        defaultOpen={false}
        testid="endpoints-panel"
        badge={<span className="text-[9.5px] text-gray-500">{endpointClusters.length} pt · {joinedCount} joint(s)</span>}
      >
        <div className="mt-1 flex items-center justify-between gap-2">
          <div className="text-[10px] text-gray-400 flex items-center gap-2">
            <span data-testid="endpoint-clusters-count">
              <strong className="text-gray-200">{endpointClusters.length}</strong> point(s)
            </span>
            <span className="text-emerald-300/80">↔ {joinedCount} joint(s)</span>
            <span className="text-amber-300/80">○ {isolatedCount} isolé(s)</span>
          </div>
          <label className="inline-flex items-center gap-1 text-[10px] cursor-pointer" title="Accrochage aux extrémités des murs existants">
            <input type="checkbox" checked={endpointSnap}
                   onChange={(e) => setEndpointSnap(e.target.checked)}
                   className="w-3 h-3 accent-emerald-500"
                   data-testid="endpoint-snap-toggle" />
            <span className={endpointSnap ? 'text-emerald-300 font-medium' : 'text-gray-400'}>
              Snap {endpointSnap ? 'ON' : 'OFF'}
            </span>
          </label>
        </div>
        {endpointSnap && (
          <div className="mt-1.5">
            <label className="text-[9.5px] text-gray-400 block mb-0.5">
              Rayon de snap : <strong>{(endpointSnapRadius * 100).toFixed(1)}%</strong>
            </label>
            <input
              type="range" min={0.005} max={0.05} step={0.001}
              value={endpointSnapRadius}
              onChange={(e) => setEndpointSnapRadius(parseFloat(e.target.value))}
              className="w-full accent-emerald-500"
              data-testid="endpoint-snap-radius"
            />
          </div>
        )}
      </FoldableSection>

      <div className="flex-1 overflow-y-auto p-2">
        {disabled ? (
          <p className="text-[10.5px] text-gray-500 italic">Liste indisponible — étapes précédentes incomplètes.</p>
        ) : walls.length === 0 ? (
          <EmptyHint kind="walls" />
        ) : (
          WALL_TYPES.map((t) => {
            const items = grouped[t.value] || [];
            if (items.length === 0) return null;
            return (
              <div key={t.value} className="mb-3">
                <div className="flex items-center gap-1.5 text-[10.5px] font-semibold mb-1"
                     style={{ color: t.color }}>
                  <span className="w-2 h-2 rounded-sm" style={{ background: t.color }} />
                  {t.label}
                  <span className="text-gray-500 font-normal">({items.length})</span>
                </div>
                <ul className="space-y-1">
                  {items.map((w, idx) => (
                    <WallRow key={w.id} wall={w} index={idx + 1}
                             isSelected={w.id === selectedWallId}
                             isHovered={w.id === hoveredWallId}
                             onHoverEnter={() => setHoveredWallId(w.id)}
                             onHoverLeave={() => setHoveredWallId(null)}
                             onSelect={() => setSelectedWallId(w.id)}
                             onTypeChange={(newType) => {
                               // V14.78 — applique l'épaisseur admin du nouveau
                               // type sauf si fixée manuellement par l'utilisateur.
                               const th = defaultWallThicknessPx(newType);
                               actions.updateWall(w.id, {
                                 type: newType,
                                 ...(th && !w.thickness_custom ? { thickness: th } : {}),
                               });
                             }}
                             onDelete={() => actions.deleteWall(w.id)} />
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const EmptyHint = ({ kind }) => (
  <div className="text-center text-[10.5px] text-gray-500 p-4 border border-dashed rounded"
       style={{ borderColor: 'var(--c-border)' }}>
    {kind === 'walls' ? (
      <>
        <Boxes className="w-5 h-5 mx-auto mb-1.5 opacity-50" />
        Aucun mur encore. Cliquez sur <span className="text-amber-300">Détecter les Murs (IA)</span> ou
        passez en mode <span className="text-amber-300">Dessiner</span>.
      </>
    ) : (
      <>
        <DoorOpen className="w-5 h-5 mx-auto mb-1.5 opacity-50" />
        Aucune menuiserie. Choisissez un picto dans la barre d&apos;outils et cliquez sur le plan pour la déposer.
      </>
    )}
  </div>
);

const WallRow = ({ wall, index, isSelected, isHovered, onHoverEnter, onHoverLeave,
                   onSelect, onTypeChange, onDelete }) => {
  const style = wallStyleFor(wall.type);
  const len = Math.sqrt((wall.x2 - wall.x1) ** 2 + (wall.y2 - wall.y1) ** 2);
  const lenPct = (len * 100).toFixed(1);
  return (
    <li
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      onClick={onSelect}
      className={`group flex items-center gap-1.5 px-1.5 py-1 rounded cursor-pointer text-[10.5px] border transition-colors ${isSelected
        ? 'bg-amber-500/15 border-amber-400/40'
        : isHovered ? 'bg-white/10 border-white/20'
                    : 'border-transparent hover:bg-white/5'}`}
      data-testid={`wall-row-${wall.id}`}
    >
      <span className="w-3 h-1 rounded-sm shrink-0" style={{ background: style.color }} />
      <span className="font-mono text-gray-400 w-5 shrink-0">#{index}</span>
      <span className="text-gray-300 flex-1 truncate" title={`Longueur ${lenPct}% de la diagonale image`}>
        {lenPct}% · {wall.source === 'ai' ? 'IA' : 'Manuel'}
      </span>
      <select
        value={wall.type}
        onChange={(e) => { e.stopPropagation(); onTypeChange(e.target.value); }}
        onClick={(e) => e.stopPropagation()}
        className="bg-transparent border rounded px-1 py-0.5 text-[10px]"
        style={{ borderColor: 'var(--c-border)' }}
        data-testid={`wall-type-${wall.id}`}
      >
        {WALL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <button type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-rose-400 opacity-60 group-hover:opacity-100 hover:text-rose-200"
              title="Supprimer ce mur"
              data-testid={`wall-delete-${wall.id}`}>
        <Trash2 className="w-3 h-3" />
      </button>
    </li>
  );
};

// =====================================================================
// OPENINGS PANEL — Sprint 3
// =====================================================================

const OpeningsPanel = ({ projectId, disabled }) => {
  const {
    openings, walls, selectedOpeningId, setSelectedOpeningId,
    hoveredOpeningId, setHoveredOpeningId, actions, lastSyncReport,
  } = usePlanEditor();

  // V14.59 Sprint 14 — Group-by toggle : 'type' (legacy) or 'wall'.
  const [groupBy, setGroupBy] = useState('type');

  const groupedByType = useMemo(() => {
    const g = { front_door: [], interior_door: [], closet_door: [], french_door: [], window: [], bay_window: [] };
    openings.forEach((o) => { (g[o.type] || g.window).push(o); });
    return g;
  }, [openings]);

  // V14.59 Sprint 14 — Group by wall_id, with an « Orphelines » bucket
  // for openings that never went through wall snap.
  const groupedByWall = useMemo(() => {
    const map = new Map();
    openings.forEach((o) => {
      const key = o.wall_id || '__orphan__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(o);
    });
    // Order : walls in their declared order first, then orphans last.
    const ordered = [];
    walls.forEach((w) => {
      if (map.has(w.id)) {
        ordered.push({ wallId: w.id, wallType: w.type, items: map.get(w.id) });
        map.delete(w.id);
      }
    });
    // Remaining keys = orphan bucket or wall_ids referencing deleted walls
    map.forEach((items, key) => {
      ordered.push({ wallId: key === '__orphan__' ? null : key, wallType: null, items });
    });
    return ordered;
  }, [openings, walls]);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="openings-panel">
      <div className="p-2.5 border-b" style={{ borderColor: 'var(--c-border)' }}>
        <h4 className="text-[12px] font-semibold flex items-center gap-1.5"
            style={{ color: 'var(--c-gold)' }}>
          <DoorOpen className="w-3.5 h-3.5" /> Menuiseries
        </h4>
        <div className="text-[10.5px] text-gray-400 mt-0.5 flex items-center justify-between gap-2">
          <span>{openings.length} élément(s)</span>
          <div className="inline-flex items-center rounded border overflow-hidden"
               style={{ borderColor: 'var(--c-border)' }}
               data-testid="openings-groupby-switch">
            <button type="button"
                    onClick={() => setGroupBy('type')}
                    className={`px-1.5 py-0.5 text-[9.5px] ${groupBy === 'type' ? 'bg-amber-500/20 text-amber-200' : 'text-gray-400 hover:bg-white/5'}`}
                    data-testid="openings-groupby-type">
              Par type
            </button>
            <button type="button"
                    onClick={() => setGroupBy('wall')}
                    className={`px-1.5 py-0.5 text-[9.5px] ${groupBy === 'wall' ? 'bg-amber-500/20 text-amber-200' : 'text-gray-400 hover:bg-white/5'}`}
                    data-testid="openings-groupby-wall">
              Par mur
            </button>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {MENUISERIE_TYPES.map((t) => (
            <span key={t.value}
                  className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: `${t.color}22`, color: t.color }}>
              <span className="w-2 h-2 rounded-sm" style={{ background: t.color }} />
              {t.short} · {groupedByType[t.value]?.length || 0}
            </span>
          ))}
        </div>
        {lastSyncReport?.created?.length > 0 && (
          <div className="mt-2 text-[10px] text-emerald-300/90 bg-emerald-500/10 border border-emerald-400/20 rounded px-1.5 py-1 flex items-start gap-1.5"
               data-testid="materialtheque-sync-badge">
            <Link2 className="w-3 h-3 mt-0.5 shrink-0" />
            <span>
              <strong>{lastSyncReport.created.length}</strong> slot(s) ajoutés à la Matériauthèque (
              {lastSyncReport.created.map((c) => c.name).join(', ')}). Assignez-leur un matériau dans
              l&apos;onglet Matériauthèque pour préciser le rendu 3D.
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        {disabled ? (
          <p className="text-[10.5px] text-gray-500 italic">Liste indisponible — étapes précédentes incomplètes.</p>
        ) : openings.length === 0 ? (
          <EmptyHint kind="openings" />
        ) : groupBy === 'type' ? (
          MENUISERIE_TYPES.map((t) => {
            const items = groupedByType[t.value] || [];
            if (items.length === 0) return null;
            return (
              <div key={t.value} className="mb-3">
                <div className="flex items-center gap-1.5 text-[10.5px] font-semibold mb-1"
                     style={{ color: t.color }}>
                  <span className="w-2 h-2 rounded-sm" style={{ background: t.color }} />
                  {t.label}
                  <span className="text-gray-500 font-normal">({items.length})</span>
                </div>
                <ul className="space-y-1">
                  {items.map((o, idx) => (
                    <OpeningRow key={o.id} opening={o} index={idx + 1}
                                projectId={projectId}
                                isSelected={o.id === selectedOpeningId}
                                isHovered={o.id === hoveredOpeningId}
                                onHoverEnter={() => setHoveredOpeningId(o.id)}
                                onHoverLeave={() => setHoveredOpeningId(null)}
                                onSelect={() => setSelectedOpeningId(o.id)}
                                onPatch={(patch) => actions.updateOpening(o.id, patch)}
                                onDelete={() => actions.deleteOpening(o.id)} />
                  ))}
                </ul>
              </div>
            );
          })
        ) : (
          // V14.59 Sprint 14 — Group-by-wall view
          groupedByWall.map((group) => {
            const isOrphan = !group.wallId;
            const wallStyle = group.wallType ? wallStyleFor(group.wallType) : null;
            const heading = isOrphan
              ? { label: 'Orphelines (sans mur parent)', color: '#9ca3af' }
              : { label: `Mur ${group.wallId.slice(-6)}${group.wallType ? ` · ${group.wallType}` : ''}`,
                  color: wallStyle?.color || '#a78bfa' };
            return (
              <div key={group.wallId || 'orphan'} className="mb-3"
                   data-testid={`openings-wall-group-${group.wallId || 'orphan'}`}>
                <div className="flex items-center gap-1.5 text-[10.5px] font-semibold mb-1"
                     style={{ color: heading.color }}>
                  {isOrphan
                    ? <AlertCircle className="w-3 h-3" />
                    : <span className="w-2 h-2 rounded-sm" style={{ background: heading.color }} />}
                  {heading.label}
                  <span className="text-gray-500 font-normal">({group.items.length})</span>
                  {!isOrphan && group.items.length >= 4 && (
                    <span className="ml-1 text-[9px] px-1 rounded bg-rose-500/15 text-rose-300"
                          title="4+ ouvertures sur ce mur — vérifiez la cohérence">
                      ⚠ saturé
                    </span>
                  )}
                </div>
                <ul className="space-y-1">
                  {group.items.map((o, idx) => (
                    <OpeningRow key={o.id} opening={o} index={idx + 1}
                                projectId={projectId}
                                isSelected={o.id === selectedOpeningId}
                                isHovered={o.id === hoveredOpeningId}
                                onHoverEnter={() => setHoveredOpeningId(o.id)}
                                onHoverLeave={() => setHoveredOpeningId(null)}
                                onSelect={() => setSelectedOpeningId(o.id)}
                                onPatch={(patch) => actions.updateOpening(o.id, patch)}
                                onDelete={() => actions.deleteOpening(o.id)} />
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const OpeningRow = ({ opening, index, projectId, isSelected, isHovered, onHoverEnter, onHoverLeave,
                      onSelect, onPatch, onDelete }) => {
  const style = openingStyleFor(opening.type);
  const { calibration } = usePlanEditor();
  // V14.78 — édition en cm quand le plan est calibré.
  const scale = calibration?.scale_m_per_norm_unit || null;
  const widthCm = scale ? Math.round((opening.width || 0) * scale * 100) : null;
  const materialthequeHref = projectId
    ? `/hub/materials/${projectId}?subrole=${encodeURIComponent(style.subrole)}`
    : null;
  return (
    <li
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      onClick={onSelect}
      className={`group rounded cursor-pointer text-[10.5px] border transition-colors px-1.5 py-1 ${isSelected
        ? 'bg-amber-500/15 border-amber-400/40'
        : isHovered ? 'bg-white/10 border-white/20'
                    : 'border-transparent hover:bg-white/5'}`}
      data-testid={`opening-row-${opening.id}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="w-3 h-1.5 rounded-sm shrink-0" style={{ background: style.color }} />
        <span className="font-mono text-gray-400 w-5 shrink-0">#{index}</span>
        <span className="text-gray-300 flex-1 truncate">{style.label}</span>
        {widthCm != null && (
          <span className="font-mono text-[9.5px] text-gray-400 shrink-0"
                data-testid={`opening-width-cm-badge-${opening.id}`}>
            {widthCm} cm
          </span>
        )}
        <select
          value={opening.type}
          onChange={(e) => { e.stopPropagation(); onPatch({ type: e.target.value }); }}
          onClick={(e) => e.stopPropagation()}
          className="bg-transparent border rounded px-1 py-0.5 text-[10px]"
          style={{ borderColor: 'var(--c-border)' }}
          data-testid={`opening-type-${opening.id}`}
        >
          {MENUISERIE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {materialthequeHref && (
          <a
            href={materialthequeHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-emerald-400 opacity-60 group-hover:opacity-100 hover:text-emerald-200 inline-flex items-center"
            title={`Voir le matériau (${style.subrole}) dans la Matériauthèque`}
            data-testid={`opening-view-material-${opening.id}`}
          >
            <ArrowUpRight className="w-3 h-3" />
          </a>
        )}
        <button type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="text-rose-400 opacity-60 group-hover:opacity-100 hover:text-rose-200"
                title="Supprimer cette menuiserie"
                data-testid={`opening-delete-${opening.id}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
      {isSelected && (
        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
          {scale ? (
            <label className="text-[9.5px] text-gray-400 flex flex-col gap-0.5"
                   onClick={(e) => e.stopPropagation()}>
              Largeur (cm)
              <input
                type="number" step={5} min={20} max={600}
                value={widthCm}
                onChange={(e) => {
                  const cm = parseFloat(e.target.value);
                  if (Number.isFinite(cm) && cm > 0) onPatch({ width: (cm / 100) / scale });
                }}
                onClick={(e) => e.stopPropagation()}
                className="bg-black/30 border rounded px-1 py-0.5 text-[10px] text-gray-100"
                style={{ borderColor: 'var(--c-border)' }}
                data-testid={`opening-width-cm-${opening.id}`}
              />
            </label>
          ) : (
            <NumberField label="Largeur" value={opening.width} step={0.005} min={0.01} max={1}
                         onChange={(v) => onPatch({ width: v })} testId={`opening-width-${opening.id}`} />
          )}
          <NumberField label="Hauteur" value={opening.height} step={0.002} min={0.005} max={0.5}
                       onChange={(v) => onPatch({ height: v })} testId={`opening-height-${opening.id}`} />
          <NumberField label="Rotation°" value={opening.rotation || 0} step={5} min={-180} max={180}
                       onChange={(v) => onPatch({ rotation: v })} testId={`opening-rotation-${opening.id}`} />
        </div>
      )}
      {isSelected && (() => {
        // V15.30 — paramétrage menuiserie : vantaux / hauteur / allège / ouvrant (→ maquette blanche + prompt).
        const spec = openingSpecFor(opening, widthCm != null ? widthCm / 100 : 0);
        const glazed = GLAZED_OPENING_TYPES.includes(opening.type);
        const isWindow = opening.type === 'window';
        return (
          <div className="mt-1.5 rounded border border-white/10 bg-black/20 p-1.5 space-y-1.5"
               onClick={(e) => e.stopPropagation()} data-testid={`opening-spec-${opening.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[9.5px] uppercase tracking-[0.14em] text-gray-400 font-semibold">Menuiserie</span>
              <span className="text-[9px]" style={{ color: spec.isDefault ? 'var(--c-text-faint)' : 'var(--c-gold)' }}
                    data-testid={`opening-spec-origin-${opening.id}`}>
                {spec.isDefault ? 'défauts du type' : 'personnalisée'}
              </span>
            </div>
            <div className={`grid gap-1.5 ${isWindow ? 'grid-cols-4' : 'grid-cols-3'}`}>
              <label className="text-[9.5px] text-gray-400 flex flex-col gap-0.5">
                Vantaux
                <input type="number" step={1} min={1} max={8} value={spec.leaves}
                       onChange={(e) => { const v = parseInt(e.target.value, 10); if (Number.isFinite(v) && v >= 1) onPatch({ leaves: Math.min(8, v) }); }}
                       className="bg-black/30 border rounded px-1 py-0.5 text-[10px] text-gray-100" style={{ borderColor: 'var(--c-border)' }}
                       data-testid={`opening-leaves-${opening.id}`} />
              </label>
              <label className="text-[9.5px] text-gray-400 flex flex-col gap-0.5">
                Hauteur (m)
                <input type="number" step={0.05} min={0.3} max={4.5} value={spec.height_m}
                       onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v) && v > 0) onPatch({ height_m: +v.toFixed(2) }); }}
                       className="bg-black/30 border rounded px-1 py-0.5 text-[10px] text-gray-100" style={{ borderColor: 'var(--c-border)' }}
                       data-testid={`opening-height-m-${opening.id}`} />
              </label>
              {isWindow && (
                <label className="text-[9.5px] text-gray-400 flex flex-col gap-0.5">
                  Allège (m)
                  <input type="number" step={0.05} min={0} max={2.5} value={spec.sill_m}
                         onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v) && v >= 0) onPatch({ sill_m: +v.toFixed(2) }); }}
                         className="bg-black/30 border rounded px-1 py-0.5 text-[10px] text-gray-100" style={{ borderColor: 'var(--c-border)' }}
                         data-testid={`opening-sill-m-${opening.id}`} />
                </label>
              )}
              <label className="text-[9.5px] text-gray-400 flex flex-col gap-0.5">
                Ouvrant
                <select value={spec.mode} onChange={(e) => onPatch({ mode: e.target.value })}
                        className="bg-black/30 border rounded px-1 py-0.5 text-[10px] text-gray-100" style={{ borderColor: 'var(--c-border)' }}
                        data-testid={`opening-mode-${opening.id}`}>
                  {OPENING_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
            </div>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[9px] leading-snug text-gray-500">
                {glazed
                  ? `Maquette : vitrage fermé teinté, ${spec.leaves} vantaux (${spec.leaves + 1} montants)${widthCm != null ? `, ${(widthCm / 100).toFixed(2)} × ${spec.height_m.toFixed(2)} m` : ''}. Décrite telle quelle à l'IA.`
                  : `Porte ${spec.height_m.toFixed(2)} m, ${spec.leaves} vantail${spec.leaves > 1 ? 'x' : ''}.`}
              </p>
              {!spec.isDefault && (
                <button type="button" onClick={() => onPatch({ leaves: undefined, height_m: undefined, sill_m: undefined, mode: undefined })}
                        className="text-[9px] text-gray-400 hover:text-gray-200 underline shrink-0"
                        data-testid={`opening-spec-reset-${opening.id}`}>
                  Défauts
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </li>
  );
};

const NumberField = ({ label, value, step, min, max, onChange, testId }) => (
  <label className="text-[9.5px] text-gray-400 flex flex-col gap-0.5"
         onClick={(e) => e.stopPropagation()}>
    {label}
    <input
      type="number"
      step={step}
      min={min}
      max={max}
      value={Number(value || 0).toFixed(3)}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      onClick={(e) => e.stopPropagation()}
      className="bg-black/30 border rounded px-1 py-0.5 text-[10px] text-gray-100"
      style={{ borderColor: 'var(--c-border)' }}
      data-testid={testId}
    />
  </label>
);

// =====================================================================
// =====================================================================
// KEYBOARD SHORTCUTS
// =====================================================================

const KeyboardShortcuts = ({ disabled }) => {
  const {
    activeSubTab, setMode,
    selectedWallId, actions,
    selectedOpeningId, polygonDraft, mode, drawWallType,
  } = usePlanEditor();

  useEffect(() => {
    if (disabled) return undefined;
    const onKey = (e) => {
      // Skip when typing in an input / select / textarea
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'select', 'textarea'].includes(tag)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 's') { setMode('select'); actions.cancelPolygonDraft(); e.preventDefault(); }
      else if (k === 'e') { setMode('erase'); actions.cancelPolygonDraft(); e.preventDefault(); }
      else if (k === 'd' && activeSubTab === 'walls') { setMode('draw'); e.preventDefault(); }
      else if (k === 'p' && activeSubTab === 'walls') {
        actions.cancelPolygonDraft();
        setMode('draw-polyline-walls');
        e.preventDefault();
      }
      else if (k === 'd' && activeSubTab === 'contour') { setMode('draw-outer-polygon'); e.preventDefault(); }
      else if (k === 't' && activeSubTab === 'contour') { setMode('draw-terrain-polygon'); e.preventDefault(); }
      else if (e.key === 'Enter' && polygonDraft.length >= 2) {
        // V14.58 Sprint 13 LOT 4 — Enter commits the current polygon /
        // polyline. Routing matches the active draw mode.
        if (mode === 'draw-polyline-walls') actions.commitWallPolyline(drawWallType);
        else if (mode === 'draw-room-polygon' && polygonDraft.length >= 3) actions.commitRoomPolygon('autre');
        else if (mode === 'draw-outer-polygon' && polygonDraft.length >= 3) actions.commitOuterPolygon();
        e.preventDefault();
      }
      else if (e.key === 'Escape' && polygonDraft.length > 0) {
        actions.cancelPolygonDraft(); e.preventDefault();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (activeSubTab === 'walls' && selectedWallId) {
          actions.deleteWall(selectedWallId); e.preventDefault();
        } else if (activeSubTab === 'openings' && selectedOpeningId) {
          actions.deleteOpening(selectedOpeningId); e.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, activeSubTab, mode, setMode, selectedWallId, selectedOpeningId, actions, polygonDraft.length, drawWallType]);

  return null;
};

export default TabStructure;
